import React, { useEffect, useMemo } from 'react';
import { Dialog } from '@mui/material';
import { STACK_STRINGS } from './constants';

// API hooks
import useGetStackById from '@src/hooks/api/queries/stacks/useGetStackById';
import useGetDefaultStack from '@src/hooks/api/queries/modes/useGetDefaultStack';
import useStackMutations from '@src/hooks/api/mutations/useStackMutations';
import useGetControlModeTemplates from '@src/hooks/api/queries/modes/useGetControlModeTemplates';

// Components
import { TemplatePicker } from './components/TemplatePicker';
import StackModalHeader from './components/StackModalHeader';
import StackModalContent from './components/StackModalContent';
import StackModalActions from './components/StackModalActions';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';
import TemplateConfirmationDialog from './components/TemplateConfirmationDialog';

// Custom hooks
import useStackModalReducer from './hooks/useStackModalReducer';

// Context
import { StackModalProvider } from './context/StackModalContext';

// Types
import { StackModalProps } from './types';

/**
 * A modal component for creating and editing stacks.
 * It allows selecting templates for each control mode in the stack.
 */
const StackModal: React.FC<StackModalProps> = ({
  stackId,
  open,
  onClose,
  isDefault,
}) => {
  // API hooks
  const { createStack, updateStack } = useStackMutations(stackId, isDefault);

  // Use different data fetching hooks based on isDefault flag
  const { data: defaultStackData, isLoading: isLoadingDefaultStack } =
    useGetDefaultStack();
  const { data: regularStackData, isLoading: isLoadingRegularStack } =
    useGetStackById({
      id: stackId!,
      enabled: !!stackId && open && !isDefault,
    });

  // Determine which data and loading state to use
  const stackData = isDefault ? defaultStackData : regularStackData;
  const isLoadingStack = isDefault
    ? isLoadingDefaultStack
    : isLoadingRegularStack;

  const { isLoading: isLoadingTemplates } = useGetControlModeTemplates();
  const isLoading = isLoadingStack || isLoadingTemplates;

  // Determine modal title based on mode
  const isEditMode = Boolean(stackId);
  const modalTitle = isDefault
    ? isEditMode
      ? STACK_STRINGS.MODAL_TITLE_EDIT_DEFAULT
      : STACK_STRINGS.MODAL_TITLE_CREATE_DEFAULT
    : isEditMode
      ? STACK_STRINGS.MODAL_TITLE_EDIT
      : STACK_STRINGS.MODAL_TITLE_CREATE;

  // State management
  const modalState = useStackModalReducer({
    stackId,
    createStack,
    updateStack,
    onClose,
    isDefault,
  });

  const { setStackName, setControlModes, resetForm } = modalState;

  // Effects
  // Initialize form values when stack data is loaded
  useEffect(() => {
    if (open) {
      if (stackData) {
        setStackName(stackData.name || '');
        setControlModes(stackData.controlModes || []);
      } else if (!stackId) {
        // Initialize with empty values for new stack
        setStackName('');
        setControlModes([]);
      }
    }
  }, [open, stackData, stackId, setStackName, setControlModes]);

  // Cleanup when modal is closed
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  // Memoized context value with improved structure
  const contextValue = useMemo(() => {
    const {
      // State
      stackName,
      nameError,
      validationErrors,
      alertMessage,
      controlModes,
      isSaving,
      isTemplatePickerOpen,
      currentControlModeIndex,
      isTemplateConfirmOpen,
      templatesForConfirmation,
      controlModeIndexForConfirmation,
      templateIdsForConfirmation,
      isDeleteConfirmOpen,
      controlModeToDelete,

      // Actions
      handleNameChange,
      handleSave,
      handleAddControlMode,
      handleDuplicateControlMode,
      handleControlModeNameChangeWithValidation,
      handleControlModeAliasChange,
      handleControlModeAliasRemoval,
      handleControlModeEnabledChange,
      handleDeleteControlMode,
      handleFieldChange,
      handleDeleteSection,
      handleTemplatesSelected,
      handleOpenTemplatePicker,
      handleCloseTemplatePicker,
      handleOpenTemplateConfirm,
      handleCloseTemplateConfirm,
      handleConfirmTemplates,
      handleOpenDeleteConfirm,
      handleCloseDeleteConfirm,
    } = modalState;

    return {
      // State
      state: {
        stackName,
        nameError,
        validationErrors,
        alertMessage,
        controlModes,
        isSaving,
        isTemplatePickerOpen,
        currentControlModeIndex,
        isTemplateConfirmOpen,
        templatesForConfirmation,
        controlModeIndexForConfirmation,
        templateIdsForConfirmation,
        isDeleteConfirmOpen,
        controlModeToDelete,
      },

      // Actions organized by domain
      actions: {
        form: {
          handleNameChange,
          handleSave,
        },
        controlMode: {
          handleAddControlMode,
          handleDuplicateControlMode,
          handleControlModeNameChangeWithValidation,
          handleControlModeAliasChange,
          handleControlModeAliasRemoval,
          handleControlModeEnabledChange,
          handleDeleteControlMode,
          handleOpenDeleteConfirm,
          handleCloseDeleteConfirm,
        },
        field: {
          handleFieldChange,
          handleDeleteSection,
        },
        template: {
          handleTemplatesSelected,
          handleOpenTemplatePicker,
          handleCloseTemplatePicker,
          handleOpenTemplateConfirm,
          handleCloseTemplateConfirm,
          handleConfirmTemplates,
        },
      },
    };
  }, [modalState]);

  return (
    <StackModalProvider value={contextValue}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth='md'
        aria-labelledby='stack-modal-title'
      >
        <StackModalHeader title={modalTitle} onClose={onClose} />
        <StackModalContent isLoading={isLoading} />
        <StackModalActions onClose={onClose} />
      </Dialog>

      <TemplatePicker />
      <TemplateConfirmationDialog />
      <DeleteConfirmationDialog />
    </StackModalProvider>
  );
};

export default React.memo(StackModal);
